import React, { useEffect } from 'react';
import { ErrorMessage, FlexRow, Grid, Header3Text } from 'ui-toolkit';
import { Field, FieldArray, FieldProps } from 'formik';
import { useSelector } from 'react-redux';
import { CreateEditAchPullDepositRow } from './CreateEditAchPullDepositRow';
import { useKeyToSelect } from '../../../../../store/createKeyedDataAsyncSlice';
import { RootStore } from '../../../../../store/types';
import { IAchPullDepositData } from '../../../../../types/order/escrow/IAchPullDepositData';
import { deleteAchDepositAction } from '../../../../../store/order/escrow/deposits/achDeposits/deleteAchDepositSlice';
import {
  sendAchDepositAction,
  sendAchDepositSlice,
} from '../../../../../store/order/escrow/deposits/achDeposits/sendAchDepositSlice';
import { BlockRedirection } from '../../../../cdBuilder/BlockRedirection';
import { HasPermissionButton } from '../../shared/HasPermissionButton';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { IPartyPerson } from '../../../../../ui-toolkit/orders/interfaces/IPartyPerson';
import { useAppDispatch } from '../../../../../store/store';

export interface ICreateEditAchPullDepositsContainerProps {
  escrowFileId: string;
  dirty: boolean;
  parties: IPartyPerson[];
  onTransactionUpdate: () => void;
}

export const CreateEditAchPullDepositsContainer: React.FC<ICreateEditAchPullDepositsContainerProps> = ({
  escrowFileId,
  dirty,
  parties,
  onTransactionUpdate,
}) => {
  const dispatch = useAppDispatch();
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedAchPullDepositsEdit]);

  const { data: achAccounts } = useKeyToSelect(
    escrowFileId,
    (store: RootStore) => store.order.escrow.getAchAccountsExcludingAD
  );

  const onRemoveAchDeposit = async (achToRemove: IAchPullDepositData) => {
    if (achToRemove.createdAt) {
      dispatch(deleteAchDepositAction(achToRemove.id));
      onTransactionUpdate();
    }
  };

  const { fetchError, fetchStatus } = useSelector(
    (store: RootStore) => store.order.escrow.deposits.sendAchDepositSlice
  );

  useEffect(() => {
    if (fetchStatus.success) {
      setTimeout(() => {
        dispatch(sendAchDepositSlice.actions.reset({}));
        onTransactionUpdate();
      }, 3000);
    }
    // eslint-disable-next-line
  }, [fetchStatus.success]);

  const onSendAchDeposit = async (achDepositId: string) => {
    await dispatch(sendAchDepositAction(achDepositId));
  };

  return (
    <>
      <ErrorMessage error={fetchError} />
      <BlockRedirection showSaveSpinner={false} showSaveSuccess={false} isDirty={dirty} />
      <FieldArray name={'achPullDeposits'}>
        {({ push, remove }) => (
          <>
            <Grid>
              <FlexRow>
                <Header3Text>ACH Deposits</Header3Text>
                <HasPermissionButton
                  hasEditPermission={hasEditPermission}
                  push={push}
                  successMessage={'Add ACH Deposit'}
                  transferType={'ach deposits'}
                  defaultObject={{ version: 'v1' }}
                />
              </FlexRow>
              <Field name={'achPullDeposits'}>
                {({ field }: FieldProps<IAchPullDepositData[]>) => (
                  <>
                    {field.value.map((achDeposit, index) => (
                      <CreateEditAchPullDepositRow
                        key={index}
                        index={index}
                        onDelete={async (index) => {
                          remove(index);
                          await onRemoveAchDeposit(achDeposit);
                        }}
                        onSend={onSendAchDeposit}
                        fieldName={'achPullDeposits'}
                        achAccounts={achAccounts || []}
                        parties={parties}
                        routingNumber={achDeposit?.account?.routingNumber}
                      />
                    ))}
                  </>
                )}
              </Field>
            </Grid>
          </>
        )}
      </FieldArray>
    </>
  );
};
